import { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';

// ---------- Tipos ----------
interface Concepto {
  id: string;
  nombre: string;
  descripcion: string;
  unidad: string;
  precio_publico: number | null;
  precio_proveedor: number | null;
  esVariable: boolean;
  categoria: string;
  moneda: 'MXN' | 'USD';
}

interface ConceptoForm {
  nombre: string;
  descripcion: string;
  precio_publico: string;
  precio_proveedor: string;
  esVariable: boolean;
  moneda: 'MXN' | 'USD';
}

const emptyForm: ConceptoForm = {
  nombre: '',
  descripcion: '',
  precio_publico: '',
  precio_proveedor: '',
  moneda: 'MXN',
  esVariable: false,
};

// ---------- Helpers ----------
const API_URL = 'https://api.airmex.mx/api/v1';

function formatPrecio(monto: number, moneda: string): string {
  const currency = moneda === 'USD' ? 'USD' : 'MXN';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(monto);
}

function getToken() {
  return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
}

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_URL}${url}`, { ...options, headers });
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: 'Error desconocido' }));
    throw new Error(error.detail || 'Error en la petición');
  }
  if (res.status === 204) return null;
  return res.json();
}

export default function CatalogoPage() {
  const { showToast } = useToast();
  const [conceptos, setConceptos] = useState<Concepto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'normal' | 'variable'>('todos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ConceptoForm>(emptyForm);
  const [importando, setImportando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<Concepto | null>(null);

  const loadConceptos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWithAuth('/conceptos/');
      setConceptos(data);
    } catch (err: any) {
      setError(err.message);
      showToast('error', 'Error al cargar conceptos', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConceptos();
  }, []);

  const filtered = useMemo(() =>
    conceptos.filter((c) => {
      const matchSearch = c.nombre.toLowerCase().includes(search.toLowerCase()) ||
        c.descripcion.toLowerCase().includes(search.toLowerCase());
      const matchTipo =
        filtroTipo === 'todos' ||
        (filtroTipo === 'normal' && !c.esVariable) ||
        (filtroTipo === 'variable' && c.esVariable);
      return matchSearch && matchTipo;
    }),
    [conceptos, search, filtroTipo]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (c: Concepto) => {
    setEditingId(c.id);
    setForm({
      nombre: c.nombre,
      descripcion: c.descripcion,
      precio_publico: c.precio_publico != null ? c.precio_publico.toString() : '',
      precio_proveedor: c.precio_proveedor != null ? c.precio_proveedor.toString() : '',
      esVariable: c.esVariable,
      moneda: c.moneda || 'MXN',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        unidad: 'pieza',
        precio_publico: form.esVariable ? null : (parseFloat(form.precio_publico) || 0),
        precio_proveedor: form.esVariable ? null : (parseFloat(form.precio_proveedor) || 0),
        esVariable: form.esVariable,
        categoria: 'Varios',
        moneda: form.moneda,
      };

      console.log('Payload enviado:', payload);  // para depuración

      if (editingId) {
        const updated = await fetchWithAuth(`/conceptos/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setConceptos(prev => prev.map(c => c.id === editingId ? updated : c));
        showToast('success', 'Concepto actualizado', `${form.nombre} se ha modificado.`);
      } else {
        const nuevo = await fetchWithAuth('/conceptos/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setConceptos(prev => [...prev, nuevo]);
        showToast('success', 'Concepto registrado', `${form.nombre} se ha agregado.`);
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handleDescargarPlantilla = async () => {
    try {
      const response = await fetch(`${API_URL}/conceptos/export`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (!response.ok) throw new Error('Error al descargar');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'conceptos.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      showToast('error', 'Error al descargar plantilla', err.message);
    }
  };

  const handleImportarExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_URL}/conceptos/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });
      if (!response.ok) throw new Error('Error al importar');
      const result = await response.json();
      showToast('success', result.mensaje);
      const data = await fetchWithAuth('/conceptos/');
      setConceptos(data);
    } catch (err: any) {
      showToast('error', 'Error al importar', err.message);
    } finally {
      setImportando(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/conceptos/${deleteTarget.id}`, { method: 'DELETE' });
      setConceptos(prev => prev.filter(c => c.id !== deleteTarget.id));
      showToast('success', 'Concepto eliminado', `${deleteTarget.nombre} se ha eliminado.`);
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <h2 className="font-heading font-semibold text-foreground-700">Error al cargar conceptos</h2>
          <p className="text-sm text-foreground-500 mt-2">{error}</p>
          <button onClick={loadConceptos} className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-medium">
            Reintentar
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Catálogo de Conceptos</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{conceptos.length} conceptos registrados</p>
          </div>
          <div className="flex gap-2">
            <button onClick={openCreate} className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
              <i className="ri-add-line mr-1.5" />Nuevo concepto
            </button>
            <button
              onClick={handleDescargarPlantilla}
              className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-download-line" /> Plantilla
            </button>
            <label className="px-4 py-2.5 rounded-lg bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer flex items-center gap-1.5">
              <i className="ri-upload-line" /> Importar Excel
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                ref={fileInputRef}
                onChange={handleImportarExcel}
                disabled={importando}
              />
            </label>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar concepto..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div className="flex rounded-lg border border-background-200 overflow-hidden">
            {(['todos', 'normal', 'variable'] as const).map((t) => (
              <button key={t} onClick={() => setFiltroTipo(t)} className={`px-3 py-2 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${filtroTipo === t ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-500 hover:bg-background-100'}`}>
                {t === 'todos' ? 'Todos' : t === 'normal' ? 'Normales' : 'Variables'}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-booklet-line text-xl text-foreground-300" /></div>
              <p className="text-sm text-foreground-500">No se encontraron conceptos.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Concepto</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Precio Público</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Precio Proveedor</th>
                    <th className="text-center text-xs font-medium text-foreground-400 uppercase px-5 py-3">Moneda</th>
                    <th className="text-center text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden sm:table-cell">Tipo</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-background-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <p className="text-sm font-medium text-foreground-800">{c.nombre}</p>
                        <p className="text-xs text-foreground-400 mt-0.5 line-clamp-1">{c.descripcion}</p>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {c.esVariable ? (
                          <span className="text-xs text-amber-600 font-medium">Variable</span>
                        ) : (
                          <span className="text-sm font-semibold text-foreground-800">{formatPrecio(c.precio_publico || 0, c.moneda)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {c.esVariable ? (
                          <span className="text-xs text-amber-600 font-medium">Variable</span>
                        ) : (
                          <span className="text-sm font-semibold text-foreground-800">{formatPrecio(c.precio_proveedor || 0, c.moneda)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-center">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${c.moneda === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {c.moneda}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center hidden sm:table-cell">
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${c.esVariable ? 'bg-amber-100 text-amber-700' : 'bg-accent-100 text-accent-700'}`}>
                          {c.esVariable ? 'Variable' : 'Normal'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Editar"><i className="ri-edit-line text-sm" /></button>
                          <button onClick={() => setDeleteTarget(c)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Eliminar"><i className="ri-delete-bin-line text-sm" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Editar concepto' : 'Nuevo concepto'} size="lg"
        footer={
          <>
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer">Cancelar</button>
            <button onClick={handleSave} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${!form.nombre.trim() ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`} disabled={!form.nombre.trim()}>{editingId ? 'Guardar cambios' : 'Registrar concepto'}</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre del concepto *</label>
            <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Excavación en terreno tipo B" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Descripción</label>
            <textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={3} maxLength={500} placeholder="Describe el concepto..." className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 resize-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Precio Público</label>
              <input type="number" value={form.precio_publico} onChange={(e) => setForm({ ...form, precio_publico: e.target.value })} disabled={form.esVariable} placeholder={form.esVariable ? 'No aplica' : 'Ej: 280'} className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 ${form.esVariable ? 'bg-background-100 border-background-200 text-foreground-400 cursor-not-allowed' : 'border-background-200 bg-background-50 text-foreground-700'}`} />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Precio Proveedor</label>
              <input
                type="number"
                value={form.precio_proveedor}
                onChange={(e) => setForm({ ...form, precio_proveedor: e.target.value })}
                disabled={form.esVariable}
                placeholder={form.esVariable ? 'No aplica' : 'Ej: 180'}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 ${form.esVariable ? 'bg-background-100 border-background-200 text-foreground-400 cursor-not-allowed' : 'border-background-200 bg-background-50 text-foreground-700'}`}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Moneda</label>
            <select
              value={form.moneda}
              onChange={(e) => setForm({ ...form, moneda: e.target.value as 'MXN' | 'USD' })}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100"
            >
              <option value="MXN">Pesos mexicanos (MXN)</option>
              <option value="USD">Dólares estadounidenses (USD)</option>
            </select>
          </div>
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <input type="checkbox" id="esVariable" checked={form.esVariable} onChange={(e) => setForm({ ...form, esVariable: e.target.checked, precio_publico: e.target.checked ? '' : form.precio_publico, precio_proveedor: e.target.checked ? '' : form.precio_proveedor })} className="w-4 h-4 rounded border-background-300 text-amber-600 focus:ring-amber-500 cursor-pointer" />
            <label htmlFor="esVariable" className="text-sm text-foreground-700 cursor-pointer">Concepto variable (sin precio fijo, se define al cotizar)</label>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} title="Eliminar concepto" message={`¿Estás seguro de eliminar "${deleteTarget?.nombre}"?`} confirmLabel="Sí, eliminar" variant="danger" />
    </DashboardLayout>
  );
}